// Тема оформления: режим, цвета дашборда, пресеты и сохранение настроек.

import type { DashboardTheme, ThemeMode } from '../types';
import { THEME_PRESETS, colorsAreTooClose, deriveCssTokens, normalizeDashboardTheme, resolveThemeMode } from '../theme';
import { setVisualContext } from '../visual-utils';
import { t } from '../i18n';

import { PREFERENCES_STORAGE_KEY } from './preferences';
import { controls } from './controls';
import { currentLocale } from './locale';
import { markDirty } from './status';
import { rerenderCachedWidgets } from './widgets';
import { state } from './state';

export const prefersDarkMedia = window.matchMedia('(prefers-color-scheme: dark)');

export function currentTheme(): DashboardTheme {
  return normalizeDashboardTheme(state.definition.theme);
}

export function saveUserPreferences(): void {
  localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(state.preferences));
}

export function resolvedThemeMode(): 'light' | 'dark' {
  return resolveThemeMode(state.preferences.themeMode, prefersDarkMedia.matches);
}

export function applyTheme(): void {
  const theme = currentTheme();
  const resolved = resolvedThemeMode();
  document.documentElement.lang = currentLocale();
  document.documentElement.dataset.theme = resolved;
  document.documentElement.dataset.themeMode = state.preferences.themeMode;
  Object.entries(deriveCssTokens(theme, resolved)).forEach(([key, value]) => {
    document.documentElement.style.setProperty(key, value);
  });
  setVisualContext(theme, currentLocale(), resolved);
  controls.settingsPrimaryColor.value = theme.primaryColor;
  controls.settingsColorWarning.hidden = !colorsAreTooClose(theme);
  updateSettingsButtonStates();
}

export function updateSettingsButtonStates(): void {
  controls.settingsLocaleRu.classList.toggle('active', currentLocale() === 'ru');
  controls.settingsLocaleEn.classList.toggle('active', currentLocale() === 'en');
  // Кнопка в шапке показывает текущий язык; нажатие переключает на другой.
  const label = t(currentLocale(), 'currentLanguage');
  controls.localeToggleCode.textContent = currentLocale().toUpperCase();
  controls.localeToggle.setAttribute('aria-label', label);
  controls.localeToggle.dataset.tooltip = label;
  controls.settingsThemeSystem.classList.toggle('active', state.preferences.themeMode === 'system');
  controls.settingsThemeLight.classList.toggle('active', state.preferences.themeMode === 'light');
  controls.settingsThemeDark.classList.toggle('active', state.preferences.themeMode === 'dark');
}

export function renderThemePresets(): void {
  controls.settingsPresets.innerHTML = THEME_PRESETS.map((preset, index) => `
    <button type="button" class="theme-preset" data-theme-preset="${index}" aria-label="Preset ${index + 1}">
      <span style="background:${preset.primaryColor}"></span>
    </button>
  `).join('');
}

export function renderAutoColorPresets(): void {
  controls.autoColorPresets.innerHTML = THEME_PRESETS.map((preset, index) => `
    <button type="button" class="theme-preset" data-auto-color-preset="${index}" aria-label="Preset ${index + 1}">
      <span style="background:${preset.primaryColor}"></span>
    </button>
  `).join('');
}

export function applyDashboardTheme(theme: DashboardTheme, dirty = true): void {
  state.definition.theme = normalizeDashboardTheme(theme);
  state.preferences.lastPrimaryColor = state.definition.theme.primaryColor;
  saveUserPreferences();
  applyTheme();
  if (dirty) markDirty();
  void rerenderCachedWidgets(true);
}

export function changeThemeMode(mode: ThemeMode): void {
  state.preferences.themeMode = mode;
  saveUserPreferences();
  applyTheme();
  void rerenderCachedWidgets(true);
}
